import React, { useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, Outlet } from 'react-router-dom';
import {
  User,
  Shield,
  Database,
  Link as LinkIcon,
  Bell,
  Smartphone,
  CreditCard,
  ChevronRight,
  Settings,
  ClipboardList,
  Columns,
  Clock,
  Building2,
  PenTool,
  Wallet,
  Sparkles,
  Users,
  Landmark,
  Target,
  Megaphone,
  CalendarClock
} from 'lucide-react';
import ProfileSettings from './Config/ProfileSettings';
import DadosBancariosView from './Config/DadosBancariosView';
import MeuDinheiroView from './Config/MeuDinheiroView';
import UserManagement from './Config/UserManagement';
import EquipeParceirosView from './Config/EquipeParceirosView';
import LogsView from './Config/LogsView';
import PipelineSettings from './Config/PipelineSettings';
import SlaSettings from './Config/SlaSettings';
import ApisView from './Config/ApisView';
import MetasGeracaoView from './Config/MetasGeracaoView';
import NotificationsView from './Config/NotificationsView';
import EmpresasParceirasView from './Config/EmpresasParceirasView';
import VigorGatewayAsaasView from './Config/VigorGatewayAsaasView';
import TermoSignatariosView from './Config/TermoSignatariosView';
import AvisosView from './Config/AvisosView';
import ConsumidoresPortalView from './Config/ConsumidoresPortalView';
import EscalasComercialView from './Config/EscalasComercialView';
import { useAuth } from '../src/contexts/AuthContext';

const ConfigView: React.FC = () => {
  // A área de conteúdo rola sozinha (overflow-y-auto): ao trocar de tela ela
  // guardava a rolagem da anterior — no celular, o cartão tocado lá embaixo
  // abria a tela nova no meio (#6140).
  const { pathname } = useLocation();
  const conteudoRef = useRef<HTMLDivElement>(null);
  useEffect(() => { conteudoRef.current?.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="flex h-[calc(100vh-112px)] gap-8 animate-fade-in text-left">
      {/* Sidebar */}
      <div className="w-64 shrink-0 space-y-2 hidden md:block h-full overflow-y-auto pr-2 pb-4">
        <h2 className="text-xl font-bold font-poppins mb-6 px-4 text-gray-900 dark:text-white">Configurações</h2>
        <ConfigLink to="/config/profile" icon={User} label="Conta & Perfil" />
        <ConfigLink to="/config/notifications" icon={Bell} label="Notificações" />

        {/* Financeiro / Dados Bancários: Parceiro e Vendedor Parceiro (recebem comissão/bônus) */}
        <RestrictedTo roles={['parceiro', 'vendedor_parceiro']}>
          <ConfigLink to="/config/financeiro" icon={Wallet} label="Meu Dinheiro" />
          <ConfigLink to="/config/dados-bancarios" icon={CreditCard} label="Dados Bancários" />
        </RestrictedTo>

        {/* Usuários: Adm, Gerente e Parceiro */}
        <RestrictedTo roles={['adm', 'gerente', 'parceiro', 'gerente_parcerias']}>
          <div className="h-px bg-gray-100 dark:bg-gray-700 my-2 mx-4"></div>
          <ConfigLink to="/config/users" icon={Shield} label="Usuários e Permissões" />
        </RestrictedTo>

        {/* Equipe de Parceiros: apenas Parceiro */}
        <RestrictedTo roles={['parceiro']}>
          <ConfigLink to="/config/equipe-parceiros" icon={Users} label="Equipe de Parceiros" />
        </RestrictedTo>

        {/* Funis e SLAs: Adm e Gerente */}
        <RestrictedTo roles={['adm', 'gerente']}>
          <ConfigLink to="/config/pipelines" icon={Columns} label="Funis e Etapas" />
          <ConfigLink to="/config/slas" icon={Clock} label="SLA / Prazos" />
          <ConfigLink to="/config/escalas" icon={CalendarClock} label="Escalas do Comercial" />
        </RestrictedTo>

        {/* Avisos: leitura para Adm, Gerente e Analista (só Adm publica) */}
        <RestrictedTo roles={['adm', 'gerente', 'analista']}>
          <ConfigLink to="/config/avisos" icon={Megaphone} label="Avisos" />
        </RestrictedTo>

        <RestrictedTo roles={['adm', 'gerente', 'analista']}>
          <ConfigLink to="/config/consumidores-portal" icon={Users} label="Consumidores" />
        </RestrictedTo>

        {/* Logs: apenas Adm — a aba "Uso" (telemetria) vive dentro dessa mesma tela */}
        <RestrictedTo roles={['adm']}>
          <ConfigLink to="/config/logs" icon={ClipboardList} label="Logs do Sistema" />
        </RestrictedTo>

        {/* Links visíveis apenas para Adm */}
        <RestrictedTo roles={['adm']}>
          <ConfigLink to="/config/apis" icon={LinkIcon} label="APIs e Webhooks" />
          <ConfigLink to="/config/termo-signatarios" icon={PenTool} label="Assinaturas do Termo" />
          <ConfigLink to="/config/empresas-parceiras" icon={Building2} label="Empresas Parceiras" />
          <ConfigLink to="/config/conta-asaas-vigor" icon={Landmark} label="Conta Asaas Vigor" />
          <ConfigLink to="/config/metas-geracao" icon={Target} label="Geração e Metas" />
        </RestrictedTo>
      </div>

      {/* Content Area */}
      <div ref={conteudoRef} className="flex-1 h-full overflow-y-auto pr-4 pb-8">
        <Routes>
          <Route index element={<ConfigOverview />} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="dados-bancarios" element={<ProtectedRouteConfig roles={['parceiro', 'vendedor_parceiro']}><DadosBancariosView /></ProtectedRouteConfig>} />
          <Route path="financeiro" element={<ProtectedRouteConfig roles={['parceiro', 'vendedor_parceiro']}><MeuDinheiroView /></ProtectedRouteConfig>} />
          <Route path="notifications" element={<NotificationsView />} />

          {/* Protected Routes inside Config */}
          <Route path="users" element={<ProtectedRouteConfig roles={['adm', 'gerente', 'parceiro', 'gerente_parcerias']}><UserManagement /></ProtectedRouteConfig>} />
          <Route path="equipe-parceiros" element={<ProtectedRouteConfig roles={['parceiro']}><EquipeParceirosView /></ProtectedRouteConfig>} />
          <Route path="pipelines" element={<ProtectedRouteConfig roles={['adm', 'gerente']}><PipelineSettings /></ProtectedRouteConfig>} />
          <Route path="slas" element={<ProtectedRouteConfig roles={['adm', 'gerente']}><SlaSettings /></ProtectedRouteConfig>} />
          <Route path="escalas" element={<ProtectedRouteConfig roles={['adm', 'gerente']}><EscalasComercialView /></ProtectedRouteConfig>} />
          <Route path="avisos" element={<ProtectedRouteConfig roles={['adm', 'gerente', 'analista']}><AvisosView /></ProtectedRouteConfig>} />
          <Route path="consumidores-portal" element={<ProtectedRouteConfig roles={['adm', 'gerente', 'analista']}><ConsumidoresPortalView /></ProtectedRouteConfig>} />
          <Route path="logs" element={<ProtectedRouteConfig roles={['adm']}><LogsView /></ProtectedRouteConfig>} />
          <Route path="apis" element={<ProtectedRouteConfig roles={['adm']}><ApisView /></ProtectedRouteConfig>} />
          <Route path="termo-signatarios" element={<ProtectedRouteConfig roles={['adm']}><TermoSignatariosView /></ProtectedRouteConfig>} />
          <Route path="empresas-parceiras" element={<ProtectedRouteConfig roles={['adm']}><EmpresasParceirasView /></ProtectedRouteConfig>} />
          <Route path="conta-asaas-vigor" element={<ProtectedRouteConfig roles={['adm']}><VigorGatewayAsaasView /></ProtectedRouteConfig>} />
          {/* gerente entra junto: a policy de escrita de geradoras_metas ja libera
              adm/gerente, e travar so em adm aqui deixaria a permissao do banco
              inalcancavel pela tela. */}
          <Route path="metas-geracao" element={<ProtectedRouteConfig roles={['adm', 'gerente']}><MetasGeracaoView /></ProtectedRouteConfig>} />
        </Routes>
      </div>
    </div>
  );
};

const ConfigLink = ({ to, icon: Icon, label }: { to: string, icon: any, label: string }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to === '/config' && location.pathname === '/config');

  // Quick fix for overview highlighting if specific path
  const activeClass = isActive ? 'bg-primary/10 dark:bg-primary/20 text-primary font-semibold' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800';

  return (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeClass}`}>
      <Icon size={18} />
      <span className="text-sm">{label}</span>
    </Link>
  );
}

const ConfigOverview = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'vendedor';

  const allSections = [
    { title: 'Conta & Perfil', desc: 'Gerencie suas informações pessoais e segurança.', icon: User, color: 'text-blue-500', to: '/config/profile', roles: ['adm', 'gerente', 'vendedor', 'analista'] },
    { title: 'Usuários e Permissões', desc: 'Gerencie quem tem acesso à plataforma.', icon: Shield, color: 'text-purple-500', to: '/config/users', roles: ['adm', 'gerente', 'parceiro', 'gerente_parcerias'] },
    { title: 'Equipe de Parceiros', desc: 'Veja os vendedores parceiros vinculados a você.', icon: Users, color: 'text-teal-500', to: '/config/equipe-parceiros', roles: ['parceiro'] },
    { title: 'APIs e Webhooks', desc: 'Configure integrações com sistemas externos.', icon: LinkIcon, color: 'text-green-500', to: '/config/apis', roles: ['adm'] },
    { title: 'Assinaturas do Termo', desc: 'Testemunhas e procurador que assinam o termo de adesão.', icon: PenTool, color: 'text-teal-500', to: '/config/termo-signatarios', roles: ['adm'] },
    { title: 'Configuração de Funis', desc: 'Gerencie pipelines e etapas de venda.', icon: Columns, color: 'text-secondary', to: '/config/pipelines', roles: ['adm', 'gerente'] },
    { title: 'SLA / Prazos', desc: 'Configure o tempo limite para etapas e ações.', icon: Clock, color: 'text-rose-500', to: '/config/slas', roles: ['adm', 'gerente'] },
    { title: 'Avisos', desc: 'Comunicados ao consumidor e à equipe interna, com confirmação de leitura obrigatória.', icon: Megaphone, color: 'text-secondary', to: '/config/avisos', roles: ['adm', 'gerente', 'analista'] },
    { title: 'Consumidores', desc: 'Contas de acesso ao Portal do Consumidor — carga inicial, provisionamento automático e listagem.', icon: Users, color: 'text-primary', to: '/config/consumidores-portal', roles: ['adm', 'gerente', 'analista'] },
    { title: 'Notificações', desc: 'Personalize como você recebe alertas.', icon: Bell, color: 'text-orange-500', to: '/config/notifications', roles: ['adm', 'gerente', 'vendedor', 'analista'] },
    { title: 'Logs do Sistema', desc: 'Auditoria de atividades e % de uso medido de cada módulo.', icon: ClipboardList, color: 'text-gray-500', to: '/config/logs', roles: ['adm'] },
    { title: 'Empresas Parceiras', desc: 'Contratos, percentuais e acessos das empresas parceiras.', icon: Building2, color: 'text-indigo-500', to: '/config/empresas-parceiras', roles: ['adm'] },
    { title: 'Conta Asaas Vigor', desc: 'Credenciais da conta que recebe o boleto único de todas as empresas parceiras.', icon: Landmark, color: 'text-emerald-600', to: '/config/conta-asaas-vigor', roles: ['adm'] },
    { title: 'Geração e Metas', desc: 'Meta de cada usina pelo laudo PVsyst e a geração mensal apurada, com correção manual quando a coleta não fecha o mês.', icon: Target, color: 'text-primary', to: '/config/metas-geracao', roles: ['adm', 'gerente'] },
  ];

  const sections = allSections.filter(s => s.roles.includes(role));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sections.map((item, i) => (
          <Link
            key={i}
            to={item.to}
            className="flex items-center gap-4 p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:border-primary/30 hover:shadow-md transition-all text-left group"
          >
            <div className={`w-12 h-12 shrink-0 rounded-xl bg-gray-50 dark:bg-gray-700 flex items-center justify-center ${item.color}`}>
              <item.icon size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">{item.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight mt-1">{item.desc}</p>
            </div>
            <ChevronRight className="text-gray-300 dark:text-gray-600 group-hover:text-primary" size={20} />
          </Link>
        ))}
      </div>
      <div className="bg-primary/5 dark:bg-primary/10 rounded-3xl p-8 flex items-center justify-between border border-primary/10 dark:border-primary/20">
        <div className="flex gap-4">
          <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-2xl flex items-center justify-center text-primary shadow-sm">
            <Smartphone size={32} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">App VigorHub Mobile</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm max-w-md">Em breve, gerencie faturas e leads diretamente do seu smartphone com notificações em tempo real.</p>
          </div>
        </div>
        <button className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-lg">
          Notificar Lançamento
        </button>
      </div>
    </div>
  );
}

// Helper Components for protection
const RestrictedTo = ({ roles, children }: { roles: string[], children: React.ReactNode }) => {
  const { profile } = useAuth();
  if (!profile || !roles.includes(profile.role)) return null;
  return <>{children}</>;
};

const ProtectedRouteConfig = ({ roles, children }: { roles: string[], children: React.ReactNode }) => {
  const { profile } = useAuth();

  if (!profile) return null; // Or loading

  if (!roles.includes(profile.role)) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-900 animate-fade-in">
        <Shield size={48} className="text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-red-900 dark:text-red-200 mb-2">Acesso Restrito</h2>
        <p className="text-red-700 dark:text-red-300">Você não tem permissão para acessar esta área.</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default ConfigView;
